'use strict';
/*global AuthUtils, OptionUtils, Utils, LocaleUtils*/
/*eslint no-undef: "error"*/
import _ from 'lodash';
import moment from 'moment-timezone';
import React, { memo, useContext, useCallback, useRef, useReducer, useEffect, useMemo, forwardRef, useState, useImperativeHandle } from 'react';
import { Loading } from '../../components/Control';
import { CheckPermission } from '../../components/Generic';
import { Tip, ContextMenu } from '../../components/Dialogs';
import { ShiftingFieldSettingDialog, ShiftingFieldContext, useFieldSetting, ShiftingFieldProvider } from './ShiftingFieldSetting';
import { EDJobPicker } from '../ed/edInputs';
import { fieldIdMap, defaultFieldSetting } from './ShiftingConstants';
import { useBooleanFlag, useForceUpdate } from '../../components/Hooks';
import { Text, TimeRange, MultipleInput2, RNumber } from '../../components/Input';

import { Popup } from '../../components/Popup';
import { CompTimeDialog, SpecialRestDialog } from './ShiftingDialog';
import { SidePanel, HourCheckSection } from './ShiftingSidePanel';
import { ShiftingContext, ControlPanelContext, RowContext, ShiftingActionContext, SidePanelContext, SidePanelProvider } from './ShiftingContext';
import { I18n } from '~components/I18n';

const {
	PERMISSION: { IS_QUERY, IS_PRINT, IS_EDIT, IS_MGT },
	allOf,
} = AuthUtils;
const { dateTime } = Utils.Format;
const { adapt } = LocaleUtils;
const { getLabel } = OptionUtils;
const locale = LocaleUtils.i18nKey();

/**
 * @param {*} enterTime 輸入時間 dd:mm
 * @param {*} workdayStime 歷日開始時間 YYYY/MM/DD hh:mm:ss
 * @param {*} date 基準日 YYYY
 * @param {*} strOptions 替換文字 { next: '次日', day: '當日', yesterday: '前日' }
 * @returns string
 */
const getDayStrByCalendarDay = (enterTime, workdayStime = '', date, strOptions = {}) => {
	if (!workdayStime) console.warn('workdayStime is not exist.');
	const _date = moment(date).format('YYYY-MM-DD');

	const _workdayStime = workdayStime.replace(/\//g, '-');
	const sDate = moment(_workdayStime).format('YYYY-MM-DD');
	let dateTime = `${sDate} ${enterTime}:00`;

	if (_workdayStime > dateTime) {
		dateTime = moment(dateTime).add(24, 'hour').format('YYYY-MM-DD HH:mm');
	}
	const enterDate = moment(dateTime).format('YYYY-MM-DD');
	let str = '';

	if (enterDate > _date) {
		str = strOptions['next'] ?? OptionUtils.getLabel('components.Input', 'next');
	} else if (enterDate === _date) {
		str = strOptions['day'] ?? OptionUtils.getLabel('components.Input', 'day');
	} else {
		str = strOptions['yesterday'] ?? OptionUtils.getLabel('components.Input', 'theDayBeforeYesterday');
	}
	return str;
};

// 處理班別簡稱
const handleLabelName = (labelName) => {
	const loacle = LocaleUtils.i18nKey();

	switch (loacle) {
		case 'en_US':
			return labelName.split(' ')[0];
		default:
			return labelName[0];
	}
};

export const handleStickyClassName = (isSticky) => {
	return isSticky ? ' -stick' : '';
};

const DayType = memo(props => {
	const { allow, denied, edit, id, removeFn, dataMap } = props;
	const { readOnly } = useContext(ShiftingGlobalContext);
	const [hover, onMouseEnter, onMouseLeave] = useBooleanFlag(false);
	const _removeDayType = useCallback(evt => {
		evt.stopPropagation();
		removeFn();
	}, []);
	const obj = dataMap[id];
	if (!obj) return null;
	return <div {...{
		className: `-dayType-point -type${id}${locale ? ` -${locale}` : ''}`,
		style: { backgroundColor: obj.leaveNameColor, color: obj.textColor },
		...!readOnly && edit && allow && !denied && { onMouseEnter, onMouseLeave },
	}}>
		{handleLabelName(adapt(obj.name))}
		{hover && edit && allow && !denied && <div className="-del fa fa-times" onClick={_removeDayType} />}
	</div>;
});
DayType.displayName = 'DayType';


const Shift = memo(props => {
	const { allow, denied, edit, shiftId, clickFn, removeFn, dataMap, date, dateData } = props;
	const { readOnly } = useContext(ShiftingGlobalContext);
	const [hover, onMouseEnter, onMouseLeave] = useBooleanFlag(false);
	const ref = useRef();
	const _clickTag = useCallback(evt => {
		clickFn(evt, shiftId);
	}, []);
	const _removeShift = useCallback(evt => {
		evt.stopPropagation();
		removeFn(evt, shiftId);
	}, [shiftId]);
	const shift = dataMap[shiftId];
	if(!shift) return '';

	const amBaseDayRangeShiftType2 = times => {
		const { s, e } = times;
		const amBaseDay = datetime => `${getLabel('am.base.dayRange', datetime.substring(0, 10))} ${datetime.substring(11, 16)}`;
		return `${amBaseDay(s)} - ${amBaseDay(e)}`;
	};

	const amBaseDayRange = times => {
		const { s, e } = times;
		const sEnterTime = moment(s).format('HH:mm');
		const eEnterTime = moment(e).format('HH:mm');
		return dateData.shiftType === 2
			? amBaseDayRangeShiftType2(times)
			: `${getDayStrByCalendarDay(sEnterTime, dateData.workdayStime, date)} ${sEnterTime} - ${getDayStrByCalendarDay(eEnterTime, dateData.workdayStime, date)} ${eEnterTime}`;
	};
	const amBaseRestRange = times => {
		const { restRule, restX, restY } = times;
		return restRule === 1
			? dateData.shiftType === 2 ? amBaseDayRangeShiftType2(times) : amBaseDayRange(times)
			: _.template(getLabel('hp.m3.m310220', 'everyHourOfWorkMinuteOfRest'))
				({ H: +restX, Hs: +restX > 1 ? 's' : '', M: +restY, Ms: +restY > 1 ? 's' : '' });
	};

	const workDayIndex = date.day();
	const workTime = shift.worktime?.[workDayIndex];

	return <div {...{
		ref,
		className: `-shift${shift.isSectionShift ? ' -section': '' }`,
		...!readOnly && edit && allow && !denied && {
			onClick: _clickTag,
		},
		onMouseEnter, onMouseLeave,
	}}>
		<div className="-txt">{adapt(shift.abbrName)}</div>
		{hover && edit && allow && !denied
			? <div className="-del fa fa-times" onClick={_removeShift} />
			: <div className="-del-sp" />}
		{hover && <Tip {...{
			stick: ref.current,
		}}>
			<div>{adapt(shift?.name)}</div>
			<div>{workTime ? OptionUtils.getLabel('hp.m3.m310220', 'workTime') + amBaseDayRange(workTime) : null}</div>
			{
				shift.rests?.[workDayIndex]?.map((rest, idx) => {
					return <div key={idx}>{OptionUtils.getLabel('hp.m3.m310220', 'restTime')}{amBaseRestRange(rest)}</div>;
				})
			}
		</Tip>}
	</div>;
});
Shift.displayName = 'Shift';

const Tag = memo(props => {
	const { allow, denied, edit, shiftId, tagId, clickFn, removeFn, dataMap } = props;
	const { readOnly } = useContext(ShiftingGlobalContext);
	const [hover, onMouseEnter, onMouseLeave] = useBooleanFlag(false);
	const _clickTag = useCallback(evt => {
		clickFn(evt, shiftId, tagId);
	}, []);
	const _removeTag = useCallback(evt => {
		evt.stopPropagation();
		removeFn(shiftId, tagId);
	}, []);
	const ref = useRef();
	const tag = dataMap[tagId];
	return <div {...{
		ref,
		className: '-tag',
		style: { backgroundColor: tag.fillColor, color: tag.labelTextColor },
		...!readOnly && edit && allow && !denied && {
			onClick: _clickTag,
		},
		onMouseEnter, onMouseLeave,
	}}>
		<div className="-txt">{adapt(tag?.name)}</div>
		{hover && edit && allow && !denied
			? <div className="-del fa fa-times" onClick={_removeTag} />
			: <div className="-del-sp" style={{ backgroundColor: tag.labelTextColor }} />}
		{hover && <Tip {...{
			stick: ref.current,
		}}>
			<div>{adapt(tag?.name)}</div>
		</Tip>}
	</div>;
});
Tag.displayName = 'Tag';

const _toLeaveRng = data => `${dateTime(data.start, false)}~${dateTime(data.end, false)} (${data.value}${getLabel('am.leaveitem.unit', data.unit)})`;
const Leave = memo(props => {
	const { data, dataMap, api } = props;
	const { id: empLeaveId, itemId: leaveitemId, wf: wfResult, detail: cachedDetail, cancels } = data;
	const ref = useRef();
	const tipRef = useRef();
	const [hover, onMouseEnter, onMouseLeave] = useBooleanFlag(false);
	const [detail, setDetail] = useState(cachedDetail);
	const [loading, setLoading] = useState(false);
	useEffect(() => {
		if (hover && !detail && !loading) {
			new H2.Ajax({
				method: 'get',
				url: `/api/${api}/tip/leaveDetail/${empLeaveId}`,
				onRequest: () => setLoading(true),
				onSuccess: detail => {
					data.detail = detail; // 把假勤 tooltip 快取在 leave 裡面
					setDetail(detail);
					setTimeout(() => tipRef.current?.position(), 0);
				},
			}).send();
		}
	}, [hover, detail, loading]);
	const leave = dataMap[leaveitemId];
	if (leaveitemId > 0 && !leave) return null;
	return <div {...{
		ref,
		className: `-leave -wfResult${wfResult}`,
		onMouseEnter, onMouseLeave,
	}}>
		<div className="-symbol">{OptionUtils.getLabel('h2.am.Shifting', 'leave')}</div>
		<div className="-txt">{!leave ? OptionUtils.getLabel('hp.m4.m410040', 'leave') : adapt(leave.name)}</div>
		{hover && <Tip {...{
			ref: tipRef,
			stick: ref.current,
			// stickDirection: 'ver',
		}}>
			{detail ? <div>
				<div>[{getLabel('am.leave.workSheetStatus', wfResult)}] {adapt(detail.itemName) || ''}</div>
				<div>{OptionUtils.getLabel('h2.am.Shifting', 'leavePeriod')} {_toLeaveRng(detail)}</div>
				{detail.cancels && (
					_.size(detail.cancels) === 1
						? <div>{OptionUtils.getLabel('h2.am.Shifting', 'cancellationTime')} {_toLeaveRng(detail.cancels[0])}</div>
						: <>
							<div>{OptionUtils.getLabel('h2.am.Shifting', 'cancellationTime')}</div>
							{_.map(detail.cancels, cancel => <div key={cancel.id}>{_toLeaveRng(cancel)}</div>)}
						</>
				)}
			</div> : <Loading />}
		</Tip>}
	</div>;
});
Leave.displayName = 'Leave';

const CellError = React.memo(props => {
	const { errors, theme } = props;
	const ref = useRef();
	const [hover, onMouseEnter, onMouseLeave] = useBooleanFlag(false);
	return <div {...{
		ref,
		className: `-err ${theme || ''}`,
		onMouseEnter, onMouseLeave,
	}}>
		{hover && <Tip {...{
			stick: ref.current,
			// stickDirection: 'bottom',
			// wrapperStyle: { maxWidth: 200 },
		}}>
			<div>{_.map(errors, (error, idx) => <div key={idx}>{error}</div>)}</div>
		</Tip>}
	</div>;
});

/*
const _toHours = (t0, t1) => {
	if(!t0 || !t1) return '';
	return _.round(_calLength(t0, t1) / 3600000, 2);
};
//*/

const SectionTimeRange = forwardRef(({
	idx,
	inputs,
	workdayStime,
	workdayEtime,
	date,
	offset = 0,
	className,
	...props
}, ref) => {

	return (
		<TimeRange {...{
			className: `H2-SectionRange${className ? ` ${className}` : ''} -gum`,
			infinity: 'none',
			ref: ele => {
				if (ref) {
					ref(ele);
					return;
				};
				if (inputs) {
					inputs.period = ele;
				}
			},
			placeholder: [
				OptionUtils.getLabel('global.normal', 'startTime'),
				OptionUtils.getLabel('global.normal', 'endTime')
			],
			autoSwitch: false,
			renderPrefix: (time, i) => {
				const enterTime = time[i];

				const str = getDayStrByCalendarDay(enterTime, workdayStime, date);

				return <>
					<div style={{ width: 50 }}></div>
					{time[i] && <span className='-label' style={{
						left: 10 + i * (140 + offset),
					}}>{time ? str : null}</span>}
				</>;
			},
			...props
		}} />
	);
});

const getSectionShiftInfo = (dateData, shiftMap) => {
	const shifts = dateData.shifts.filter(x => shiftMap[x.shiftId].isSectionShift).map(x => shiftMap[x.shiftId]);
	const workTimePeriods = shifts.map(shift => {
		return {
			period: [shift.s.split(' ')[1], shift.e.split(' ')[1]]
		};
	});
	const restPeriods = shifts.reduce((outpre, outcur) => {
		return [...outpre, ...(outcur?.rests?.[0]?.reduce((pre, cur, index, arr) => {
			return [
			...pre,
			{
				period: [cur.s.split(' ')[1], cur.e.split(' ')[1]]
			}
			];
		}, []) || [])];
	}, []);
	return {
		workTimePeriods,
		restPeriods
	};
};

const checkOverlap = (timeSegments) => {
	if (timeSegments.length === 1) return false;

	timeSegments.sort((timeSegment1, timeSegment2) =>
	  timeSegment1[0].localeCompare(timeSegment2[0])
	);

	for (let i = 0; i < timeSegments.length - 1; i++) {
	  const currentEndTime = timeSegments[i][1];
	  const nextStartTime = timeSegments[i + 1][0];

	  if (currentEndTime > nextStartTime) {
		return true;
	  }
	}
	return false;
};

const checkTimeStampOverLap = (workTimeArr, restTimeArr) => {

	// 休息時間不能橫跨兩個班別
	for(const x of restTimeArr) {
		let overlapCount = 0;
		for(const y of workTimeArr) {
			if(checkOverlap([x, y])) {
				overlapCount++;
			}
			if(overlapCount > 1) {
				Utils.noticeError(null, OptionUtils.getLabel('h2.am.Shifting', 'customShiftFailureTheBreakOverlaps'));
				return false;
			}
		}
	}
	return true;
};

const SectionPopup = ({ close, _stampOn, date, empData, reloadOptions, shiftMap, shiftMax, stick, ...props}) => {
	const { setNewShift, setCalendarLoading } = useContext(ShiftingGlobalContext);
	const workTime = useRef();
	const restTime = useRef();
	const { dateData } = props;

	useEffect(() => {
		const { workTimePeriods, restPeriods } = getSectionShiftInfo(dateData, shiftMap);
		workTime.current.setValue(workTimePeriods);
		restTime.current.setValue(restPeriods);
	}, [dateData, shiftMap]);

	return (
		<Popup {...{
			stick,
			className: '-menu H2-Shift-time-dialog',
			height: 360,
			width: 475,
			onOutsideClick: close
		}}>
			<div className='-title'>
				<span>{OptionUtils.getLabel('h2.am.Shifting', 'customizeYourClass')}</span>
				<span className='-close fal fa-times' onClick={e => close(e)}></span>
			</div>
			<div className='-content'>
				<div className='row'>
					<div className='col-xs-24' style={{
						marginBottom: 6
					}}>{OptionUtils.getLabel('shift.am.Shifting', 'workingHours')}</div>
				</div>
				<div className='row'>
					<div className='col-xs-24'>
						<MultipleInput2 {...{
							min: shiftMax, singleAdder: false,
							max: 4,
							ref: el => workTime.current = el,
							// readOnly: saving,
							// initValue: rest,
						}}>{({idx, inputs, ctrlBtns, initValue}) => {
							return <div className='row'  style={{marginBottom: 4}}>
									<div className='col-xs-21'>
										<SectionTimeRange
											{...dateData}
											inputs={inputs}
											offset={50}
											labelWidth={100}
											validate={{
												required: idx === 0
											}}
										/>
									</div>
							</div>;
						}}</MultipleInput2>
					</div>
				</div>
				<div className='row'>
					<div className='col-xs-24' style={{
						marginBottom: 8
					}}>{OptionUtils.getLabel('shift.am.Shifting', 'breaks')}</div>
				</div>
				<div className='row'>
					<div className='col-xs-24'>
						<MultipleInput2 {...{
							min: 1, singleAdder: false,
							ref: el => restTime.current = el,
							// readOnly: saving,
							// initValue: rest,
						}}>{({idx, inputs, ctrlBtns, initValue}) => {
							return <div className='row'  style={{marginBottom: 4}}>
										<div className='col-xs-21'>
											<SectionTimeRange {...dateData} inputs={inputs} labelWidth={100} offset={50} />
									</div>
								<span className={'col-xs-3'} style={{paddingTop: 4}}>{ctrlBtns}</span>
							</div>;
						}}</MultipleInput2>
					</div>

				</div>
			</div>
			<div className='-footer'>
				<button className='btn btn-primary' style={{marginRight: 24}} onClick={async (e) => {
					e.stopPropagation();
					e.persist();
					let error = 0;
					!workTime.current.validate() && error++;
					!restTime.current.validate() && error++;
					if (error) return;

					const workTimeArr = workTime.current.getValue()?.map(x => x.period).filter(x => x[0] && x[1]) || [];
					const restTimeArr = restTime.current.getValue()?.map(x => x.period).filter(x => x[0] && x[1]) || [];

					const validOverlap = checkTimeStampOverLap(workTimeArr, restTimeArr);
					if (!validOverlap) {
						return;
					}

					const data = {
						// shiftJson: [
						// 	{"shift_s":"07:00" ,"shift_e":"19:00"},
						// 	{"shift_s":"20:00" ,"shift_e":"21:00"}
						// ],
						shiftJson: workTimeArr.map(x => ({ "shift_s": x[0] ,"shift_e": x[1] })),
						// actJson: [
						// 	{"act_s":"12:00" ,"act_e":"13:00"},
						// 	{"act_s":"14:00" ,"act_e":"15:00"}
						// ],
						actJson: restTimeArr.map(x => ({ "act_s": x[0] ,"act_e": x[1] })),
						date: date.format('YYYY-MM-DD'),
						empId: empData.emp.id
					};

					const newShiftData = await setNewShift(data);
					if (newShiftData.RETURN_CODE !== '00') {
						Utils.noticeError(null, OptionUtils.getLabel('h2.am.Shifting', 'customClassFailed') + newShiftData.RETURN_MSG);
						setCalendarLoading(false);
					} else {
						const _shiftMap = await reloadOptions();
						_stampOn(e, newShiftData.RETURN_DATA.map(x => _shiftMap[x.id]), true, _shiftMap);
						setCalendarLoading(false);
						close();
					}

				}}>{OptionUtils.getLabel('h2.am.Shifting', 'confirm')}</button>
				<button className='btn btn-default' onClick={e => close(e)}>{OptionUtils.getLabel('global.normal', 'cancel')}</button>
			</div>
		</Popup>
	);
};

const mergeShiftDataAndGetArr = (workDay, ...datas) => {
	const toDate = (time) => {
		return moment(time).format('HH:mm');
	};

	const workArr = [];
	const restArr = [];

	for(let data of datas) {
		for(const item of data) {
			workArr.push([toDate(item.s), toDate(item.e)]);
			restArr.push(...(item?.rests?.[workDay] || []).map(x => {
				if (x.s && x.e) {
					return [toDate(x.s), toDate(x.e)];
				}
				return [];
			}));
		}
	}

	return {
		workArr,
		restArr
	};
};

const Cell = memo(props => {
	// console.debug(`Don't see me too much.`);

	const { date, edit, cachedDates, dateData, empData } = props;
	const { refreshErrCount, readOnly, setCalendarLock , setNewShift, setCalendarLoading } = useContext(ShiftingGlobalContext);
	const { rebuild, reCalcTotalHours } = useContext(RowContext);
	const { shiftMap, calendarLeaveMap, workLabelMap, leaveitemMap, api } = useContext(ShiftingContext);
	const {
		stampOn,
		removeDayType,
		clickShift, removeShift,
		clickTag, removeTag,
		reloadOptions,
		closeCleanBtn
	} = useContext(ShiftingActionContext);
	const controlPanelCtx = useContext(ControlPanelContext);
	const cellRef = useRef();
	const [showTip, setShowTip] = useState(false);
	const [hover, setHover] = useState(false);
	const workDayIndex = date.day();

	const { allow, denied, section, errors, shiftType } = dateData;
	const forceUpdate = useForceUpdate(); // TODO 避免這樣做...嗎？

	// NOTE 標籤雖然放在 array 裡，但目前的邏輯跟操作仍只考慮單一標籤！
	const _stampOn = useCallback((evt, shifts, callNewShiftApi, _shiftMap) => {
		let value = controlPanelCtx;

		const currentShiftMap = _shiftMap || shiftMap;

		if (shifts) {
			if (callNewShiftApi) {
				value = { ...controlPanelCtx, shifts };
			} else {
				controlPanelCtx.shifts = shifts;
			}
		}
		const [updateTarget, errCountDiff] = stampOn(evt, currentShiftMap, value, cachedDates, date, dateData);
		if (updateTarget) {
			reCalcTotalHours(); // 如果只是替換標籤的話，目前也是會重算時數 (但這是多餘的)
			if (updateTarget === 'row') rebuild();
			else if (updateTarget === 'cell') forceUpdate();
		}
		if (errCountDiff) refreshErrCount();
	}, [shiftMap]);

	const _removeDayType = useCallback(evt => {
		const [, errCountDiff] = removeDayType(evt, shiftMap, cachedDates, date, dateData);
		reCalcTotalHours();
		rebuild();
		if (errCountDiff) refreshErrCount();
	}, [shiftMap, cachedDates, date, dateData]);

	const handleNewSectionShift = useCallback(async (merge = true) => {
		const oldData = dateData.shifts.map(x => shiftMap[x.shiftId]);
		const newData = controlPanelCtx.shifts;

		const { workArr, restArr } = merge ? mergeShiftDataAndGetArr(workDayIndex, oldData, newData) : mergeShiftDataAndGetArr(workDayIndex, newData);

		const data = {
			shiftJson: workArr.map(x => ({
				"shift_s": x[0],
				"shift_e": x[1]
			})),
			actJson: restArr.map(x => ({
				"act_s": x[0],
				"act_e": x[1]
			})),
			date: date.format('YYYY-MM-DD'),
			empId: empData.emp.id
		};
		const newShiftData = await setNewShift(data);
		if (newShiftData.RETURN_CODE !== '00') {
			Utils.noticeError(null, OptionUtils.getLabel('h2.am.Shifting', 'customClassFailed') + newShiftData.RETURN_MSG);
			setCalendarLoading(false);
			return {};
		} else {
			const _shiftMap = await reloadOptions();
			setCalendarLoading(false);
			// const returnData = newShiftData.RETURN_DATA.map(x => _shiftMap[x.id]).filter(x => {
			// 	return !oldData.find(y => y.id === x.id);
			// });

			return {
				newShiftData: newShiftData.RETURN_DATA.map(x => _shiftMap[x.id]),
				newShiftMap: _shiftMap
			};
		}
	}, [setNewShift, reloadOptions, setCalendarLoading, controlPanelCtx.shifts, date, dateData.shifts, empData.emp.id, shiftMap, workDayIndex]);

	const _clickShift = useCallback(async (evt, shiftId) => {
		let updateTarget;
		let errCountDiff;

		// clickShift 只處理替換單個標籤，如果非單個標籤會給上一層處理
		if (shiftType === 3 && controlPanelCtx.shifts.length === 1) {
			// evt.persist();
			evt.stopPropagation();
			const { newShiftData, newShiftMap } = await handleNewSectionShift(false);
			if (newShiftData && newShiftMap) {
				const [_updateTarget, _errCountDiff] = clickShift(evt, newShiftMap, {
					...controlPanelCtx,
					shifts: newShiftData
				}, cachedDates, date, dateData, shiftId);
				updateTarget = _updateTarget;
				errCountDiff = _errCountDiff;
			}
		} else {
			const [_updateTarget, _errCountDiff] = clickShift(evt, shiftMap, controlPanelCtx, cachedDates, date, dateData, shiftId);
			updateTarget = _updateTarget;
			errCountDiff = _errCountDiff;
		}

		if (updateTarget) {
			reCalcTotalHours(); // 如果只是替換標籤的話，目前也是會重算時數 (但這是多餘的)
			if (updateTarget === 'row') rebuild();
			else if (updateTarget === 'cell') {
				forceUpdate();
			}
		}
		if (errCountDiff) refreshErrCount();
		rebuild();
	}, [shiftMap, controlPanelCtx, cachedDates, date, dateData, clickShift, shiftType, handleNewSectionShift, forceUpdate, rebuild, reCalcTotalHours, refreshErrCount]);

	const _removeShift = useCallback((evt, shiftId) => {
		const [updateTarget, errCountDiff] = removeShift(evt, shiftMap, controlPanelCtx, cachedDates, date, dateData, shiftId);
		reCalcTotalHours();
		if (updateTarget === 'row') rebuild();
		else if (updateTarget === 'cell') forceUpdate();
		if (errCountDiff) refreshErrCount();
	}, [shiftMap, controlPanelCtx, cachedDates, date, dateData]);

	const _clickTag = useCallback((evt, shiftId, tagId) => {
		const [updateTarget] = clickTag(evt, controlPanelCtx, dateData, shiftId, tagId);
		if (updateTarget) forceUpdate();
	}, [controlPanelCtx, dateData, forceUpdate, clickTag]);

	const _removeTag = useCallback((shiftId, tagId) => {
		removeTag(dateData, shiftId, tagId);
		forceUpdate();
	}, [dateData]);

	// return ;
	const onMouseEnter = () => {
		setHover(true);
	};
	const onMouseLeave = () => setHover(false);

	const openPopup = (e) => {
		e.stopPropagation();
		setShowTip(true);
		setCalendarLock(true);
	};

	const onCellClick = async (e) => {
		if (shiftType === 3 && controlPanelCtx.shifts.length) {
			e.persist();
			// 單一標籤走 clickShift
			const { newShiftData, newShiftMap } = await handleNewSectionShift();
			if (newShiftData && newShiftMap)  {
				_stampOn(e, newShiftData, true, newShiftMap);
				rebuild();
			}
		} else {
			_stampOn(e);
		}
	};

	const _shifts = useMemo(() => {
		const _sortShiftInCell = (shiftMap, weekday, shifts) => {
			shifts.sort((a, b) => {
				const aDateStr = shiftMap[a.shiftId]?.worktime[weekday].s;
				const bDateStr = shiftMap[b.shiftId]?.worktime[weekday].s;
				return aDateStr === bDateStr ? 0 : aDateStr < bDateStr ? -1 : 1;
			});
		};
		_sortShiftInCell(shiftMap, date.day(), dateData.shifts);
		return dateData.shifts;
	}, [dateData.shifts, shiftMap, date]);

	return (
		<div {...{
			className: `-cell${denied ? ` -denied${denied !== 5 ? ' -denied-bg' : ''}` : ''}${date.day() === 0 ? ' -thick-r' : ''}`,
			...!readOnly && edit && allow && !denied && { onClick: async (e) => onCellClick(e) },
			ref: cellRef,
			onMouseEnter,
			onMouseLeave
		}}>
			{dateData.calendarLeaveId
				? <DayType {...{
					edit, allow, denied,
					id: dateData.calendarLeaveId,
					removeFn: _removeDayType,
					dataMap: calendarLeaveMap,
				}} />
				: <div className={'-dayType-point'} />}
			{denied && denied !== 5
				&& <div className="-deniedTxt">{adapt(DENIED_MAP[denied].name)}</div>
				|| null}
			{
				shiftType === 3 && dateData.shifts.length === 0 && !readOnly && edit && allow && !denied && (
					<div style={{
						color: 'rgb(3, 134, 244)',
						fontSize: 14
					}}>
						{OptionUtils.getLabel('h2.am.Shifting', 'timeSlotScheduling')}
					</div>
				) || null
			}
			{
				shiftType === 3 && !readOnly && edit && allow && !denied && hover && (
					<span
						className="-sb fa fa-pen-alt fa-lg"
						onClick={(e) => {
							openPopup(e);
							closeCleanBtn?.();
						}} />
				) || null
			}
			{showTip && <div style={{ display: 'none' }} onClick={e => e.stopPropagation()}>
				<SectionPopup {...{
					stick: cellRef.current,
					_stampOn,
					// close: () => {}
					close: (e) => {
						e?.stopPropagation();
						setHover(false);
						setShowTip(false);
						setCalendarLock(false);
					},
					reloadOptions,
					date,
					empData,
					dateData,
					shiftMap,
					shiftMax: controlPanelCtx.shiftMax
				}} />
			</div>}
			{_.map(_shifts, shift => <React.Fragment key={shift.shiftId}>
				<Shift {...{
					edit, allow, denied,
					shiftId: shift.shiftId,
					clickFn: _clickShift,
					// clickFn: () => {},
					removeFn: _removeShift,
					dataMap: shiftMap,
					date,
					dateData
				}} />
				{shift.tagIds && _.map(shift.tagIds, tagId => <Tag {...{
					key: tagId,
					edit, allow, denied, tagId,
					shiftId: shift.shiftId,
					clickFn: _clickTag,
					removeFn: _removeTag,
					dataMap: workLabelMap,
				}} />)}
			</React.Fragment>)}
			{_.map(dateData.leaves, leave => <Leave {...{
				key: leave.id,
				data: leave, // 會把假勤 tooltip 快取在 leave 裡面
				dataMap: leaveitemMap,
				api
			}} />)}
			{denied === 5
				&& <div style={{ paddingTop: 6 }}>
					<img src="img/shape.svg" alt="shape" />
				</div>
				|| null}
			{section > 0 && <div className={`-sec -sec${section % 2}`} />}
			{!_.isEmpty(errors) && <CellError errors={errors} />}
		</div>
	);
});
Cell.displayName = 'Cell';

const checkHasSectionDate = (dates, empDataDates) => {
	const _dates = [];
	for(const date of dates) {
		const formatDate = date.format('YYYY-MM-DD');
		if (empDataDates[formatDate].shiftType === 3) {
			_dates.push(date);
		}
	}
	return _dates;
};

const RowInfo = memo(props => {
	const { refreshErrCount, readOnly, setNewShift, setCalendarLoading } = useContext(ShiftingGlobalContext);
	const { onNameClick, onHourClick, reloadOptions, stampOn, beforeOnNameClick } = useContext(ShiftingActionContext); // TODO onNameClick
	const { rebuild, rerender } = useContext(RowContext);
	const { api, shiftMap } = useContext(ShiftingContext);
	const controlPanelCtx = useContext(ControlPanelContext);
	const { fieldSetting } = useFieldSetting();
	const [showCompDialog, setShowCompDialog] = useState(false);
	const [showSpecialRestDialog, setShowSpecialRestDialog] = useState(false);
	const { idx, empData, totalHours, $shiftError, $hourWarning, $hourError, dates, children, workHourDiff } = props;
	const { emp, $edit: edit, dates: cachedDates } = empData;
	const period = useMemo(() => {
		return [dates[0].format(ISO_FMT), dates[dates.length - 1].format(ISO_FMT)];
	}, [dates]);

	const _onNameClick = useCallback(evt => {
		onNameClick?.(evt, empData);
	}, [empData, onNameClick]);

	const _onHourClick = useCallback(async (evt) => {
		const edit = !empData.viewMode && empData.$edit;
		if (!edit) return;
		// 時段轉換相關，資料由後端檢查
		// const dateStr = dates.map(x => x.format('YYYY-MM-DD'));
		const sectionDate = checkHasSectionDate(dates, empData.dates);
		if (sectionDate.length > 0 && controlPanelCtx.shifts.length) {
			evt.persist();
			const newData = controlPanelCtx.shifts;
			const datas = sectionDate.map(date => {
				const formatDate = date.format('YYYY-MM-DD');
				const oldData = cachedDates[formatDate].shifts.map(x => shiftMap[x.shiftId]);
				const workDayIndex = date.day();
				const { workArr, restArr } = mergeShiftDataAndGetArr(workDayIndex, newData, oldData);
				return {
					shiftJson: workArr.map(x => ({
						"shift_s": x[0],
						"shift_e": x[1]
					})),
					actJson: restArr.map(x => ({
						"act_s": x[0],
						"act_e": x[1]
					})),
					date: date.format('YYYY-MM-DD'),
					empId: empData.emp.id
				};
			});
			const newShiftDatas = await setNewShift(datas);
			const _shiftMap = await reloadOptions(datas);
			const { sectionShiftMap } = sectionDate.reduce((pre, cur, index) => {
				const formatDate = cur.format('YYYY-MM-DD');
				const errorDates = [
					...pre.errorDates
				];
				const sectionShiftMap = {
					...pre.sectionShiftMap,
				};
				if (newShiftDatas[index].RETURN_CODE !== '00') {
					errorDates.push(formatDate);
				} else {
					sectionShiftMap[formatDate] = newShiftDatas[index].RETURN_DATA.map(x => _shiftMap[x.id]);
				}
				return {
					errorDates,
					sectionShiftMap
				};
			}, {
				errorDates: [],
				sectionShiftMap: {}
			});
			dates.forEach(date => {
				const formatDate = date.format('YYYY-MM-DD');
				const dateData = cachedDates[formatDate];
				const value = { ...controlPanelCtx, shifts: sectionShiftMap[formatDate] || [] };
				if (dateData.shiftType === 3) {
					stampOn(evt, _shiftMap, value, cachedDates, date, dateData);
				}
			});

			const { action, target } = onHourClick?.(evt, empData, {
				...controlPanelCtx,
				sectionShiftMap: sectionShiftMap
			}, _shiftMap) || {};

			refreshErrCount();
			// target 暫時只有 row
			if (action === 'rerender') rerender();
			else if (action === 'rebuild') rebuild();

			setCalendarLoading(false);
			return;
		}
		const { action, target } = onHourClick?.(evt, empData) || {};
		refreshErrCount();
		// target 暫時只有 row
		if (action === 'rerender') rerender();
		else if (action === 'rebuild') rebuild();
	}, [onHourClick, empData, cachedDates, controlPanelCtx, dates, rebuild, refreshErrCount, reloadOptions, rerender, setCalendarLoading, setNewShift, shiftMap, stampOn]);

	const curKey = useMemo(() => {
		const settings = Object.values(fieldSetting || 0).flat().filter(x => x.checked);
		const nameClickSort = ['emp.empNoAndName', 'emp.empNo', 'emp.name', 'emp.enName'];
		let curKey;

		for(let i = 0; i < settings.length; i++) {
			const findIndex = nameClickSort.indexOf(settings[i].fieldId);
			if (findIndex !== -1) {
				curKey = nameClickSort[findIndex];
				break;
			}
		}
		return curKey;
	}, [fieldSetting]);

	const getNameActionProps = useCallback((key) => {
		const isJudegeBeforeClick = beforeOnNameClick ? beforeOnNameClick(empData) : true;
		const isClick = (key === curKey) && isJudegeBeforeClick;

		return {
			className: `-cell -thick-r${isClick ? ' -clickable': ' -no-action'}`,
			...onNameClick && isClick && { onClick: _onNameClick },
		};
	}, [_onNameClick, curKey, empData, onNameClick, beforeOnNameClick]);

	const CompsMap = {
		'emp.empNoAndName': (
			<div {...getNameActionProps('emp.empNoAndName')}>
				<div className="-name">{_.isEqual(user.locale, 'en_US') ?  <>{emp.enName || emp.name}</> : <>{emp.name || emp.enName}</>}</div>
				<div className="-empNo">{emp.empNo}</div>
				{!edit && <div className="-non-edit">*{OptionUtils.getLabel('h2.am.Shifting', 'noEditingPermissions')}</div>}
			</div>
		),
		'emp.empNo': (
			<div {...getNameActionProps('emp.empNo')}>
				{emp.empNo}
			</div>
		),
		'emp.name': (
			<div {...getNameActionProps('emp.name')}>
				{emp.name || emp.enName}
			</div>
		),
		'emp.enName': (
			<div {...getNameActionProps('emp.enName')}>
				{emp.enName || '-'}
			</div>
		),
		'emp.jobId': (
			<div className='-cell -thick-r -no-action'>{emp.jobName || EDJobPicker.text(emp.jobId) || '-'}</div>
		),
		'emp.deptId': (
			<div className='-cell -thick-r -no-action'>{LocaleUtils.adapt(emp.dept1Name) || '-'}</div>
		),
		specialRest: (
			<div
				className='-cell -thick-r -clickable link'
				onClick={() => {
					setShowSpecialRestDialog(true);
				}}
			>
				<I18n key1='h2.am.Shifting' key2='tempHours' options={{'temp1': emp.specialRest ,'s': _.isEqual(user.locale, 'en_US') && emp.specialRest > 1 ? 's': ''}} />
			</div>
		),
		compTime: (
			<div
				className='-cell -thick-r -clickable link'
				onClick={() => {
					setShowCompDialog(true);
				}}
			>
				<I18n key1='h2.am.Shifting' key2='tempHours' options={{'temp1': emp.compTime,'s': _.isEqual(user.locale, 'en_US') && emp.compTime > 1 ? 's': ''}} />
			</div>
		),
		'totalHours': (
			<div {...{
				className: `-cell -thick-r${!readOnly && edit ? '' : ''}`,
				...!readOnly && edit && onHourClick && { onClick: _onHourClick },
			}}>
				<I18n key1='h2.am.Shifting' key2='tempHours' options={{'temp1': totalHours, 's': _.isEqual(user.locale, 'en_US') && totalHours > 1 ? 's': ''}} />
			</div>
		),
		cardHours: (
			<div className='-cell -thick-r -no-action'><I18n key1='h2.am.Shifting' key2='tempHours' options={{'temp1': emp.cardHours, 's': _.isEqual(user.locale, 'en_US') && emp.cardHours > 1 ? 's': ''}} /></div>
		),
		"workHourInfo.workHour": (
			<div className='-cell -thick-r -no-action'>
				<I18n key1='h2.am.Shifting' key2='tempHours' options={{'temp1': emp.workHourInfo?.workHour || 0,  's': _.isEqual(user.locale, 'en_US') && (emp.workHourInfo?.workHour || 0) > 1 ? 's': ''}} />
			</div>
		),
		workHourDiff: (
			<div className='-cell -thick-r -no-action'>
				<span className={workHourDiff > 0 ? '' : '-red'}>
					<I18n key1='h2.am.Shifting' key2='tempHours' options={{'temp1': workHourDiff, 's': _.isEqual(user.locale, 'en_US') && (workHourDiff > 1 ||  workHourDiff < -1)? 's': ''}} />
				</span>
			</div>
		),
		"workHourInfo.leave1": (
			<div className='-cell -thick-r -no-action'>
				<I18n key1='h2.am.Shifting' key2='tempDays' options={{'temp1': emp.workHourInfo?.leave1 || 0, 's': _.isEqual(user.locale, 'en_US') && (( emp.workHourInfo?.leave1 || 0) > 1) ? 's': ''}} />
			</div>
		),
		"workHourInfo.leave2": (
			<div className='-cell -thick-r -no-action'>
				<I18n key1='h2.am.Shifting' key2='tempDays' options={{'temp1': emp.workHourInfo?.leave2 || 0, 's': _.isEqual(user.locale, 'en_US') && (( emp.workHourInfo?.leave2 || 0) > 1) ? 's': ''}} />
			</div>
		),
		"workHourInfo.leave3": (
			<div className='-cell -thick-r -no-action'>
				<I18n key1='h2.am.Shifting' key2='tempDays' options={{'temp1': emp.workHourInfo?.leave3 || 0, 's': _.isEqual(user.locale, 'en_US') && (( emp.workHourInfo?.leave3 || 0) > 1) ? 's': ''}} />
			</div>
		),
		"workHourInfo.leave4": (
			<div className='-cell -thick-r -no-action'>
				<I18n key1='h2.am.Shifting' key2='tempDays' options={{'temp1': emp.workHourInfo?.leave4 || 0, 's': _.isEqual(user.locale, 'en_US') && (( emp.workHourInfo?.leave4 || 0) > 1) ? 's': ''}} />
			</div>
		),
		"workHourInfo.leave5": (
			<div className='-cell -thick-r -no-action'>
				<I18n key1='h2.am.Shifting' key2='tempDays' options={{'temp1': emp.workHourInfo?.leave5 || 0, 's': _.isEqual(user.locale, 'en_US') && (( emp.workHourInfo?.leave5 || 0) > 1) ? 's': ''}} />
			</div>
		),
	};
	return <>
		<div className={`-info -left${handleStickyClassName(fieldSetting.freezeStart)}`}>
			<div className="-cell -idx">
				{idx + 1}
				{($shiftError || $hourError || $hourWarning)
					&& <CellError {...{
						theme: !$shiftError && !$hourError ? '-hourWarning' : '',
						errors: [[
							...($hourError || $hourWarning) ? [OptionUtils.getLabel('h2.am.Shifting', 'violationOfWorkingHourRules')] : [],
							...$shiftError ? [OptionUtils.getLabel('h2.am.Shifting', 'scheduleError')] : [],
						].join('、')],
					}} />}
			</div>
			{
				fieldSetting?.start.map(item => {
					if (!item.checked) return null;
					return <React.Fragment key={item.fieldId}>{CompsMap[item.fieldId]}</React.Fragment>;
				})
			}
		</div>
		{children}
		<div className={`-info -right${handleStickyClassName(fieldSetting.freezeEnd)}`}>
			{
				fieldSetting?.end.map(item => {
					if (!item.checked) return null;
					return <React.Fragment key={item.fieldId}>{CompsMap[item.fieldId]}</React.Fragment>;
				})
			}
		</div>
		{
			showCompDialog ? <CompTimeDialog
				close={() => setShowCompDialog(false)}
				emp={emp}
				api={api}
				period={period}
			/> : null
		}
		{
			showSpecialRestDialog ? <SpecialRestDialog
				close={() => setShowSpecialRestDialog(false)}
				emp={emp}
				api={api}
				period={period}
			/> : null
		}
</>;
});
RowInfo.displayName = 'RowInfo';

const RowContent = memo(props => {
	const { dates, empData } = props;
	// NOTE empData.dates 是所有這個人已經載入的資料!!

	const edit = !empData.viewMode && empData.$edit;
	return _.map(dates, date => <Cell {...{
		key: date.valueOf(),
		date, edit,
		cachedDates: empData.dates,
		dateData: empData.dates[date.format('YYYY-MM-DD')],
		empData,
	}} />);
});
RowContent.displayName = 'RowContent';

const ISO_FMT = 'YYYY-MM-DD';
const _calcTotalHours = (shiftMap, start, end, dates) => {
	// 重要！不能直接改 start
	const incDate = start.clone().add(-1, 'day');
	let totalHours = 0;
	_.times(end.diff(incDate, 'day'), () => {
		incDate.add(1, 'day');
		const dateData = dates[incDate.format(ISO_FMT)];
		if (!dateData) return;
		// 如果那天有班別資料，不管狀態是什麼都還是要算時數 (單目前還沒有固定班資料)
		dateData.workHours = 0;
		for (let shift of dateData.shifts) {
			if (shift.workHours === undefined) {
				const obj = shiftMap[shift.shiftId];
				if (!obj?.worktime) {
					shift.workHours = 0;
					continue;
				}
				const weekday = incDate.weekday();
				shift.workHours = Number(obj.worktime[weekday]?.hours) || 0;
			}
			dateData.workHours += shift.workHours;
			// 排除行事曆別為例假日、休息日、國定假日、空班
			if (!_.includes([4, 3, 2, 5], dateData.calendarLeaveId))
				totalHours += shift.workHours;
		}
		// 跟已排時數一樣取兩位數
		dateData.workHours = _.round(dateData.workHours, 2);
	});
	// 跟 nydia 確認後取兩位數
	// [上午11:24] nydia.ni[倪嫚霙] 跟班別時間一樣 顯示到第2位就好囉 感謝
	return _.round(totalHours, 2);
};
const _calcWorkHourDiff = (empData) => {
	return empData.totalHours - (empData.emp?.workHourInfo?.workHour ||  0);
};
const Row = memo(props => {
	// console.debug('Row!');

	// 注意！dates 是全元件共通的參照，不要直接 mutate 它！
	const { idx, dates, empData, $hourWarning, $hourError } = props;
	const rerender = useForceUpdate();
	const [rebuildKey, rebuild] = useReducer(x => x + 1, 0);
	const { shiftMap } = useContext(ShiftingContext);

	// NOTE empData.dates 是所有這個人已經載入的資料!!
	const start = dates[0];
	const end = dates[dates.length - 1];

	const rowContext = useRef({}).current;
	rowContext.rebuild = rebuild;
	rowContext.rerender = rerender;
	rowContext.reCalcTotalHours = useCallback(() => {
		empData.totalHours = _calcTotalHours(shiftMap, start, end, empData.dates);
		empData.workHourDiff = _calcWorkHourDiff(empData);
		rerender();
	}, [shiftMap, start, end, empData, rerender]);

	return <div key={rebuildKey} className="-row">
		<RowContext.Provider value={rowContext}>
			{/* 拆成 RowInfo 跟 RowContent 是不希望更新已排時數的時候，讓每天的 Cell 都重新評估是否 render */}
			<RowInfo {...{
				$shiftError: empData.$shiftError, $hourWarning, $hourError,
				idx, empData,
				totalHours: empData.totalHours,
				workHourDiff: empData.workHourDiff,
				dates
			}}>
				<RowContent dates={dates} empData={empData} />
			</RowInfo>
		</RowContext.Provider>
	</div>;
});
Row.displayName = 'Row';

const Grid = React.memo(({ children }) => {
	const [position, setPosition] = useState({
		scrollTop: 0,
		scrollLeft: 0
	});
	const gridRef = useRef();
	useEffect(() => {
		gridRef.current.scrollTop = position.scrollTop;
		gridRef.current.scrollLeft = position.scrollLeft;
	}, [children, position]);

	return <div className="-grid" ref={gridRef} onScroll={(e) => {
		setPosition({
			scrollTop: gridRef.current.scrollTop,
			scrollLeft: gridRef.current.scrollLeft
		});
	}}>{ children }</div>;
});
const toTop = [r1 => r1.lockTop, r2 => !r2.lockTop];
const checkShiftTypeFromSortedData = (sortedData, dateStr) => {
	for(const data of sortedData) {
		if (data.dates[dateStr].shiftType === 3) {
			return true;
		}
	}
	return false;
};

const HeaderCell = ({children, tip, ...props}) => {
	const [hover, setHover] = useState(false);
	const ref = useRef();

	return (
		<div
			ref={ref}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			{...props}
		>
			{children}
			{
				tip && hover ? <Tip {...{
					stick: ref.current,
					stickDirection: 'right',
					stickAlign: -1,
				}}>{tip}</Tip> : null
			}
		</div>
	);
};

const shreshold = 100;
const Calendar = memo(props => {
	// console.debug('Calendar!');
	const { dates, data: rawData, isFiltering } = props;
	const forceUpdate = useForceUpdate();
	const { refreshErrCount, readOnly, setShiftEmpsDlg, setNewShift, setCalendarLoading } = useContext(ShiftingGlobalContext);
	const { onDateClick, reloadOptions, stampOn } = useContext(ShiftingActionContext);
	const { sort, setSort: _setSort } = useContext(ControlPanelContext);
	const controlPanelCtx = useContext(ControlPanelContext);
	const { fieldSetting, fieldIdMap } = useFieldSetting();
	const { shiftMap } = useContext(ShiftingContext);
	const [rebuildKey, rebuild] = useReducer(x => x + 1, 0);

	const sortByKey = useCallback((sortKey) => {
		const [key, order] = sort;
		_setSort([sortKey, key === sortKey ? (order === 'asc' ? 'desc' : 'asc') : 'asc']);
		props.setPaging(1);
		forceUpdate();
	}, [sort]);
	// const sortByTotalHours = useCallback(evt => {
	// 	const [key, order] = sort;
	// 	_setSort(['totalHours', key === 'totalHours' ? (order === 'asc' ? 'desc' : 'asc') : 'asc']);
	// 	forceUpdate();
	// }, [sort]);
	const sortedData = useMemo(() => {
		const [key, order] = sort;
		const lists = toTop.map(f => rawData.filter(f));
		const fieldIdSortKey = fieldIdMap[key]?.fieldIdSortKey;
		return [...lists[0], ..._.orderBy(lists[1], [fieldIdSortKey], order)]
			.slice((props.paging - 1) * shreshold, props.paging * shreshold);
	}, [rawData, sort, props.paging]);

	const _onDateClick = useCallback(async (evt, date) => {
		const dateStr = evt.currentTarget.dataset.date;
		const workDayIndex = date.day();
		const formatDate = date.format('YYYY-MM-DD');

		if (checkShiftTypeFromSortedData(sortedData, dateStr) && controlPanelCtx.shifts.length) {
			evt.persist();
			const newData = controlPanelCtx.shifts;
			// 如果 empData 中有時段相關的必須載入
			const datas = sortedData.map(empData => {
				const oldData = empData.dates[dateStr].shifts.map(x => shiftMap[x.shiftId]);
				const { workArr, restArr } = mergeShiftDataAndGetArr(workDayIndex, oldData, newData);
				return {
					shiftJson: workArr.map(x => ({
						"shift_s": x[0],
						"shift_e": x[1]
					})),
					actJson: restArr.map(x => ({
						"act_s": x[0],
						"act_e": x[1]
					})),
					date: date.format('YYYY-MM-DD'),
					empId: empData.emp.id
				};
			});
			const newShiftDatas = await setNewShift(datas);
			const _shiftMap = await reloadOptions();
			const { sectionShiftMap } = sortedData.reduce((pre, cur, index) => {
				const errorEmp = [...pre.errorEmp];
				const sectionShiftMap = {...pre.sectionShiftMap};
				if (newShiftDatas[index].RETURN_CODE !== '00') {
					errorEmp.push(cur.emp);
				} else {
					sectionShiftMap[cur.emp.id] = newShiftDatas[index].RETURN_DATA.map(x => _shiftMap[x.id]);
				}
				return {
					errorEmp,
					sectionShiftMap
				};
			}, {
				errorEmp: [],
				sectionShiftMap: {}
			});

			sortedData.forEach(empData => {
				const edit = !empData.viewMode && empData.$edit;
				if (!edit) return;
				const dateData = empData.dates[formatDate];
				const value = { ...controlPanelCtx, shifts: sectionShiftMap[empData.emp.id] || [] };
				if (dateData.shiftType === 3) {
					stampOn(evt, _shiftMap, value, empData.dates, date, dateData);
				}
			});

			const { action, target } = onDateClick?.(evt, dateStr, sortedData, {
				...controlPanelCtx,
				sectionShiftMap,
			}, _shiftMap) || {};
			refreshErrCount();
			// target 暫時只有 row
			if (action === 'rerender') {/*rerender();*/ }
			else if (action === 'rebuild') rebuild();
			setCalendarLoading(false);
			return;
		}
		const { action, target } = onDateClick?.(evt, dateStr, sortedData) || {};
		refreshErrCount();
		if (action === 'rerender') throw new Error('not support!');
		else if (action === 'rebuild') rebuild();
	}, [onDateClick, sortedData]);

	const renderField = (fieldSetting = [], user) => {
		const [sortKey] = sort;
		return fieldSetting.map(item => {
			if (!item.checked) return null;
			return (
				<HeaderCell
					className={`-cell -thick-r -sortable${sortKey === item.fieldId ? ` -${sort[1]}` : ''}`}
					style={_.isEqual(user.locale, 'en_US') ? !(sortKey === item.fieldId) ? {wordBreak: 'break-word', height:70}  : {height:70}: {}}
					onClick={() => sortByKey(item.fieldId)}
					key={item.fieldId}
					tip={fieldIdMap[item.fieldId]?.tip}
				>
					{fieldIdMap[item.fieldId]?.name}
				</HeaderCell>
			);
		});
	};

	if (_.isEmpty(sortedData))
		return <div className={'-noticeWrapper'}>
			<div className={'-msg msg blue'}>
				<div>
					<i className={'fa fa-info-circle'} />
					{isFiltering ? OptionUtils.getLabel('h2.am.Shifting', 'ifThereIsNoInformationThat') : OptionUtils.getLabel('h2.am.Shifting', 'ifThereIsNoDataThat')}
				</div>
				{!isFiltering && <button className={'btn btn-white'} style={{ marginTop: 20 }} onClick={setShiftEmpsDlg}>{OptionUtils.getLabel('h2.am.Shifting', 'selectTheEmployee')}</button>}
			</div>
		</div>;

	return <Grid>
		<div className="-header -stick-top">
			<div className="-row">
				<div className={`-info -left${handleStickyClassName(fieldSetting.freezeStart)}`}>
					<div className="-cell -idx" style={_.isEqual(user.locale, 'en_US') ? {height:70}: {}}
					>#</div>
					{renderField(fieldSetting.start, user)}
				</div>
				{_.map(dates, date => <div {...{
					style: _.isEqual(user.locale, 'en_US') ? {height:70}: {},
					key: date.valueOf(),
					className: `-cell${!readOnly && onDateClick ? '' : ''}${date.day() === 0 || date.day() === 6 ? ' -weekend' : ''}${date.day() === 0 ? ' -thick-r' : ''}`,
					...!readOnly && onDateClick && { onClick: (e) => _onDateClick(e, date) },
					'data-date': date.format(ISO_FMT),
				}}>
					{date.format('MM/DD')}
					({LocaleUtils.i18nIdx() === 1 ? OptionUtils.getLabel('global.week', (date.day() + 6) % 7) : OptionUtils.getLabel('global.week', (date.day() + 6) % 7).slice(-1)})
				</div>)}
				<div className={`-info -right${handleStickyClassName(fieldSetting.freezeEnd)}`}>
					{renderField(fieldSetting?.end, user)}
				</div>
			</div>
		</div>
		<div key={rebuildKey} className="-body">
			{_.map(sortedData, (empData, idx) => <Row {...{
				key: `${empData.emp.id}_${empData.$key}`,
				idx: idx + (props.paging - 1) * shreshold, dates,
				empData,
				..._.pick(empData, '$hourWarning', '$hourError'),
			}} />)}
		</div>
	</Grid>;
});
Calendar.displayName = 'Calendar';

const calcErrCount = (dates, data) => {
	let errCount = 0;
	const dateStrs = _.map(dates, date => date.format(ISO_FMT));
	_.each(data, empData => {
		if (_.some(dateStrs, dateStr => !!empData.dates[dateStr]?.errors)) {
			errCount++;
			empData.$shiftError = true;
		} else {
			empData.$shiftError = false;
		}
	});
	return errCount;
};

const newSectionShift = (url, data, setLoading) => {
	return new Promise ((res, rej) => {
		new H2.Ajax({
			url,
			method: 'post',
			data: data,
			onRequest: () => setLoading(true),
			onSuccess: (data) => {
				setLoading(false);
				res(data);
			},
			onError: (err) => {
				setLoading(false);
				rej(err);
			},
			errMsg: OptionUtils.getLabel('h2.am.Shifting', 'failedToCreateATag'),
			errCode: {},
		}).send();
	});
};

const ShiftingGlobalContext = React.createContext({});
ShiftingGlobalContext.displayName = 'ShiftingGlobalContext';
const Shifting = memo(forwardRef((props, ref) => {
	const {
		sessKey, preparing, readOnly,
		start, end, locale, data, style,
		isCtrl = true,
		sideData, unsetSideData, setEmpHourResults, saveTmp, skipHour,
		setShiftEmpsDlg, reCalcErrorCount,
		openStatisticsDlg,
		className,
		noEdit,
		keepData,
	} = props;
	const [calendarLoading, setCalendarLoading] = useState(false);
	const [calendarLock, setCalendarLock] = useState(false);
	const shiftingCtx = useContext(ShiftingContext);
	const sidePanelRef = useRef();
	const [kw, setKw] = useState('');
	const [columnSettingDlg, setColumnSettingDlg] = useState(false);
	const { showBtn } = useFieldSetting();
	const [paging, setPaging] = useState(1);
	const filteredData = useMemo(() => {
		if (_.isEmpty(kw)) return [...data];
		// escapeRegExp 來自 mootools
		const regExp = new RegExp(kw.escapeRegExp(), 'i');
		return _.filter(data, ({ emp }) => regExp.test(emp.name) || regExp.test(emp.enName) || regExp.test(emp.empNo));
	}, [data, kw]);
	const dates = useMemo(() => {
		if (!start || !end) return [];
		const _start = moment(start);
		const _end = moment(end);
		const days = _end.diff(_start, 'day', false) + 1;
		return _.times(days, i => _start.clone().add(i, 'days').locale(locale));
	}, [start, end, locale]);
	const maxPage = Math.ceil((filteredData || []).length / shreshold);
	const [errCount, setErrCount] = useState(0);
	const refreshErrCount = useCallback(() => {
		setErrCount(calcErrCount(dates, data));
	}, [dates, data]);
	useEffect(() => {
		setErrCount(calcErrCount(dates, data));
		// _calcTotalHours 會把 empData.dates 裡面也押上 workHours
		_.each(data, empData => {
			empData.totalHours = _calcTotalHours(shiftingCtx.shiftMap, moment(start, ISO_FMT), moment(end, ISO_FMT), empData.dates);
			empData.workHourDiff = _calcWorkHourDiff(empData);
		});
	}, [dates, data]);
	useEffect(() => {
		if (_.isFunction(reCalcErrorCount))
			reCalcErrorCount();
	}, [errCount]);
	useImperativeHandle(ref, () => ({
		updateSidePanel: () => {
			if (sidePanelRef.current)
				sidePanelRef.current.updateSidePanel();
		},
	}), []);
	useEffect(() => { if (maxPage && paging > maxPage) setPaging(1); }, [maxPage, paging]);
	useEffect(() => {
		if (!preparing) {
			setCalendarLock(false);
		}
	}, [preparing, setCalendarLock]);

	const globalCtx = useRef({}).current;
	globalCtx.readOnly = readOnly;
	globalCtx.refreshErrCount = refreshErrCount;
	globalCtx.setShiftEmpsDlg = setShiftEmpsDlg;
	globalCtx.setCalendarLoading = setCalendarLoading;
	globalCtx.setNewShift = async (data) => {
		try {
			let res;
			if (Array.isArray(data)) {
				res = await newSectionShift(`/api/${shiftingCtx.api}/newShifts`, data, setCalendarLoading);
			} else {
				res = await newSectionShift(`/api/${shiftingCtx.api}/newShift`, data, setCalendarLoading);
			}
			return res;
		} catch (error) {
			return {
				RETURN_MSG: OptionUtils.getLabel('h2.am.Shifting', 'otherErrors')
			};
		}
	};
	globalCtx.setCalendarLock = setCalendarLock;

	return <ShiftingGlobalContext.Provider value={globalCtx}>
		<div {...{
			className: `H2-Shifting -wrapper ${className ? className: ''}`,
			...style && { style },
		}}>
			{calendarLoading && <div className="-loading">
				<Loading size={'3x'} />
			</div>}
			{calendarLock && <div className="-loading"></div>}
			{isCtrl && <div className="flex-row -ctrl">
				<Text {...{
					width: 192,
					prefix: { ico: 'search' },
					placeholder: OptionUtils.getLabel('h2.am.Shifting', 'enterAnEmployeeKeywordQuery'),
					onEditing: txt => setKw(txt || ''),
					onBlur: () => {
						setKw(txt => txt.trim());
					},
					validate: { max: 50 },
				}} />
				{ !noEdit && <div {...{
					className: `btn btn-white ${preparing ? 'disabled' : ''}`,
					...!preparing && { onClick: setShiftEmpsDlg },
				}}>{OptionUtils.getLabel('h2.am.Shifting', 'editTheEmployee')}</div> }
				<div className={'-gum'} />
				{/*errCount !== 0 && <div style={{
					color: '#EA475B',
				}}><i className={'fa fa-exclamation-triangle'} /> 班表檢查錯誤 ({errCount})
				</div>*/}
				{!preparing && maxPage > 1 && <div style={{ display: 'flex', alignItems: 'center' }}>
					{paging > 1 && <>
						<span className="btn -sb1 btn-ico btn-white fas fa-arrow-alt-to-left"
							onClick={() => setPaging(paging - 1)}
							style={{ height: 24, padding: '4px 3px 2px', width: 24 }} />&nbsp;
					</>}
					<small>
						<RNumber value={paging} width={(34 + (((maxPage + '').length - 1) * 4)) * 2}
							validate={{ required: true, max: maxPage, min: 1 }}
							onUpdate={p => p == paging || setPaging(p)}
							suffix={{
								className: 'text-center', text: `/ ${maxPage}`,
								width: 30 + (((maxPage + '').length - 1) * 4),
							}}
							style={{ fontSize: 12, height: 24, lineHeight: '17px' }} />
					</small>&nbsp;
					{paging < maxPage && <>
						<span className="btn -sb1 btn-ico btn-white fas fa-arrow-alt-to-right"
							onClick={() => setPaging(paging + 1)}
							style={{ height: 24, padding: '4px 3px 2px', width: 24 }} />&nbsp;
					</>}
					<small className="msg sm gray50">
						共 <strong className="color-blue">{filteredData.length}</strong> 筆
					</small>
				</div>}
				<CheckPermission permission={allOf({ [shiftingCtx.api.toUpperCase().substring(0, 7)]: IS_EDIT })}>
					{showBtn ? <div className='-shifting-btn' onClick={() => {
						setColumnSettingDlg(true);
					}}>
						<i className='fal fa-cog' />
						{OptionUtils.getLabel('h2.am.Shifting', 'fieldSettings')}
					</div> : null}
				</CheckPermission>
			</div>}
			{preparing
				? <Loading size={'3x'} />
				: <>
					{sideData && <SidePanel {...{
						key: `${sideData.emp.id}_${sideData.$key}`,
						ref: sidePanelRef,
						sessKey, dates, sideData, saveTmp, skipHour,
						close: unsetSideData,
						setEmpHourResults,
						openStatisticsDlg,
						keepData,
						style: {
							position: 'absolute',
							top: 1,
							bottom: 0,
							right: 0,
							width: 400,
							boxShadow: '0 18px 30px 0 rgba(0, 0, 0, 0.38)'
						}
					}} />}
					<Calendar {...{
						dates, data: filteredData, paging, setPaging,
						isFiltering: kw !== '',
						calendarLoading
					}} />
				</>}
			{
				columnSettingDlg ? <ShiftingFieldSettingDialog {...{
					close: () => setColumnSettingDlg(false)
				}} /> : null
			}
		</div>
	</ShiftingGlobalContext.Provider>;
}));
Shifting.displayName = 'Shifting';

/**
7)已設定的班別數量 (1~4)
8)變形工時分段
 */
const DENIED_MAP = { 1: { name: { zh_TW: '未到職', zh_CN: '未到职', en_US: 'Failed to report to duty' } }, 2: { name: { zh_TW: '固定班', zh_CN: '固定班', en_US: 'Fixed shifts' } }, 3: { name: { zh_TW: '離職', zh_CN: '离职', en_US: 'Resignation' } }, 4: { name: { zh_TW: '留停', zh_CN: '留停', en_US: 'Leave without pay' } }, 5: { name: { zh_TW: '已超過修改期限', zh_CN: '已超过修改期限', en_US: 'The deadline for modifications has passed.' } }, 6: { name: { zh_TW: '不需排班', zh_CN: '不需排班', en_US: 'Non-shift' } } };

const _Factory = {
	genUnGroupedOption: () => ({
		id: -1, name: OptionUtils.getLabel('hp.m3.m310230', 'shiftGroupIsNotSet'),
		shiftMax: 2, // 每日可排班別數
		groupPeriod: 1, // 固定週期
		sMonth1: 0, sDay1: 1, // 本月一日
		eMonth1: 0, eDay1: 99, // 至本月月底
		shiftIds: [], // 無
		workLabelIds: [], // 無
		active: 1,
	}),
};

const _Utils = {
	/**
	 * 每日相同在前端做展開
	 * @param {[]} shifts will be mutated!
	 * @returns {[]} shifts
	 */
	extractWorkTime: (shifts) => {
		_.each(shifts, shift => {
			if (shift.worktimeSetting !== 0) return;
			let sample;
			sample = _.find(shift.worktime); // first none-nil
			shift.worktime = {};
			for (let i = 0; i < 7; i++)
				shift.worktime[i] = { ...sample, weekday: i };
			sample = _.find(shift.allowOt); // first none-nil
			shift.allowOt = {};
			for (let i = 0; i < 7; i++)
				shift.allowOt[i] = { ...sample, weekday: i };
			if (shift.rests) {
				sample = _.find(shift.rests); // first none-nil
				shift.rests = {};
				for (let i = 0; i < 7; i++) {
					shift.rests[i] = _.cloneDeep(sample);
					_.each(shift.rests, rest => rest.weekday = i);
				}
			}
			if (shift.inhibitOts) {
				sample = _.find(shift.inhibitOts); // first none-nil
				shift.inhibitOts = {};
				for (let i = 0; i < 7; i++) {
					shift.inhibitOts[i] = sample;
					_.each(shift.inhibitOts, inhibitOt => inhibitOt.weekday = i);
				}
			}
		});
		return shifts;
	},
};

export default { Factory: _Factory, Utils: _Utils };
export {
	Shifting, ShiftingContext, ControlPanelContext, RowContext, RowContent, ShiftingGlobalContext,
	ShiftingActionContext, DENIED_MAP, HourCheckSection, _calcTotalHours, _calcWorkHourDiff, Row,
	SectionTimeRange, ShiftingFieldContext, useFieldSetting, ShiftingFieldProvider, SidePanelContext,
	SidePanelProvider, HeaderCell, fieldIdMap, defaultFieldSetting, handleLabelName, getSectionShiftInfo, getDayStrByCalendarDay
};
export * from './ShiftingSidePanelComps';
